import { KT_PILOT_URL, isPilotEnabled } from './ktPilot';

export type MenuBadgeKey = 'not_reported_production_questions';

export interface MenuItem {
	path?: string;
	label: string;
	children?: MenuItem[];
	requiredGroup?: string;
	/** When set, the sidebar shows a count badge for this nav item. */
	badge?: MenuBadgeKey;
	/** Renders as a plain anchor instead of a router Link — `path` is a full
	 *  URL on another host, which react-router cannot route to. */
	external?: boolean;
	/** Show the entry only when this returns true. Used by the Kit Tracking
	 *  pilot, which is switched off by clearing a chart value. */
	visible?: () => boolean;
}

export const menu: MenuItem[] = [
	{
		label: 'Production',
		children: [
			{ path: '/orders', label: 'Orders' },
			{ path: '/orders/line-items', label: 'Line Items' },
			{ path: '/production/queues', label: 'Queues' },
			{ path: '/program-scoring', label: 'Program Scoring' },
			{
				path: '/production/production-questions',
				label: 'Questions',
				badge: 'not_reported_production_questions',
			},
		],
	},
	{
		label: 'Material',
		children: [
			{ path: '/material/vendor-materials', label: 'Vendor Materials' },
			{ path: '/material/orders', label: 'Orders' },
			{ path: '/material/materials', label: 'Inventory' },
			{ path: '/material/material-reservations', label: 'Reservations' },
			{ path: '/material/requests', label: 'Requests' },
			{
				path: '/material/shop-floor-requests',
				label: 'Shop Floor Requests',
			},
			{ path: '/material/receiving', label: 'Receiving' },
			{ path: '/material/certificates', label: 'Certificates' },
			{ path: '/admin/locations/manage', label: 'Locations' },
		],
	},
	{
		label: 'Machines',
		children: [
			{ path: '/machines', label: 'Machines' },
			{ path: '/cycle-time', label: 'Cycle Times' },
			{ path: '/versabuilt/mill-automation-systems', label: 'VersaBuilt' },
		],
	},
	{
		label: 'Workholding',
		children: [{ path: '/workholding/multigrip', label: 'MultiGrip' }],
	},
	{
		label: 'Quality',
		children: [
			{ label: 'Gages', path: '/quality-gages' },
			{ label: 'Ballooning', path: '/ballooning' },
			{ label: 'Traveler Audit', path: '/quality/traveler-audit' },
			{
				label: 'Keyence Validation Reports',
				path: '/quality/keyence-validation-report',
			},
		],
	},
	{
		label: 'Audit',
		children: [{ path: '/audit/robot-programs', label: 'Robot Programs' }],
	},
	{
		label: 'Scheduling',
		children: [
			{
				label: 'Overview',
				children: [
					{ path: '/scheduling/dashboard', label: 'Dashboard' },
					{ path: '/scheduling/jobs', label: 'Jobs' },
					{ path: '/scheduling/exceptions', label: 'Exceptions' },
					{ path: '/scheduling/queues', label: 'Queues' },
				],
			},
			{
				label: 'Schedule',
				children: [
					{ path: '/scheduling/job-schedule', label: 'Job Schedule' },
					{ path: '/scheduling/staff-overview', label: 'Staff Overview' },
					{ path: '/scheduling/capacity', label: 'Capacity' },
					{ path: '/scheduling/headroom', label: 'Headroom' },
				],
			},
			{
				label: 'Configuration',
				children: [
					{ path: '/scheduling/tasks', label: 'Tasks' },
					{ path: '/scheduling/machines', label: 'Machines' },
					{ path: '/scheduling/calendars', label: 'Calendars' },
					{ path: '/scheduling/scheduler', label: 'Scheduler' },
				],
			},
		],
	},
	{
		label: 'Kit Tracking',
		requiredGroup: 'Shop Managers',
		children: [{ path: '/admin/kit-staging', label: 'Staging Areas' }],
	},
	{
		label: 'Floor',
		children: [
			{ path: 'tablet/operations', label: 'Home' },
			// Pilot entry (PTB-6854) — the floor asked for a way in from here
			// rather than only from the Operations header. Retires itself; see
			// config/ktPilot.
			{
				path: KT_PILOT_URL,
				label: 'Kit Tracking (new)',
				external: true,
				visible: isPilotEnabled,
			},
		],
	},
	{
		label: 'Admin',
		requiredGroup: 'Shop Managers',
		children: [
			{
				label: 'Reports',
				children: [
					{ path: '/admin/reports/job-bottleneck', label: 'Job Bottleneck' },
					{
						path: '/admin/reports/shop-performance',
						label: 'Shop Performance',
					},
					{
						path: '/admin/reports/material-movements',
						label: 'Material Movements',
					},
					{
						path: '/admin/reports/pending-cuts',
						label: 'Pending Cuts',
					},
					{
						path: '/production/kitting-times',
						label: 'Kitting Times',
						// Breaks kitting times down per station, so it keeps the
						// role that gates the rest of kit tracking even though
						// the Admin menu above already carries it.
						requiredGroup: 'Shop Managers',
					},
				],
			},
			{ path: '/admin/ledger-exports', label: 'Ledgers' },
			{ path: '/admin/settings', label: 'Settings' },
			{ path: '/scheduling/staff', label: 'Scheduling Staff' },
			{ path: '/scheduling/roles', label: 'Scheduling Roles' },
		],
	},
];
